import React, { FunctionComponent, useEffect, useState } from 'react';
// import { useTheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { useMutation, useQuery } from '@tanstack/react-query';

// import { fetch_all, fetch_one } from '../lib/server_helper';
import { loginAtom } from '../../lib/auth';
import { fetchAll, fetchOne } from '../../lib/v31lib';

import { Line, CartesianGrid, XAxis, YAxis, Bar, ComposedChart, Tooltip, TooltipProps, PieChart, Pie, Cell, BarChart } from 'recharts';
import {
  ValueType,
  NameType,
} from 'recharts/types/component/DefaultTooltipContent';

import {
  Box,
  CircularProgress,
  Grid
} from '@mui/material';

import {
  Page,
  PieLegend,
  StyledButton,
  StyledSelect,
  Typography
} from '../../components/interface';

import { BookYear } from '../../models/BookYear';

// const { DateTime } = require("luxon");

type Props = {};
type RawExportStat = {
  custom_result: any
};
type Stat = {
  name: string;
  stock_value: number;
  amount: number;
};
type PieStat = {
  name: string;
  value: number;
  color: string;
};
type BarStat = {
  name: string;
  value: number;
  valueM1?: number;
  valueM2?: number;
};

const COLOURS = [
  '#2E7D32', // green
  '#1565C0', // blue
  '#EF6C00', // orange
  '#C62828', // red
  '#6A1B9A', // purple
  '#00838F', // teal
  '#AD1457', // magenta
  '#5D4037', // brown
  '#9E9D24', // olive
  '#00695C', // dark teal
  '#283593', // indigo
  '#D84315', // deep orange
  '#455A64', // blue grey
];

export const ExitExitStats: FunctionComponent<Props> = () => {
  // const theme = useTheme();
  
  const [login, setLogin] = useAtom(loginAtom);
  const [bookYear, setBookYear] = useState<number>();
  const [bookYearMinus1, setBookYearMinus1] = useState<number>();
  const [bookYearMinus2, setBookYearMinus2] = useState<number>();
  const [exportUrl, setExportUrl] = useState<string>();
  const [pieB1Stats, setPieB1Stats] = useState<PieStat[]>();
  const [pieB2Stats, setPieB2Stats] = useState<PieStat[]>();
  const [pieB3Stats, setPieB3Stats] = useState<PieStat[]>();
  const [barStats, setBarStats] = useState<BarStat[]>();
  const [barTotals, setBarTotals] = useState<{b1: number; b2: number; b3: number}>({b1: 0, b2: 0, b3: 0});
  const [onlyActive, setOnlyActive] = useState<boolean>(true);

  const {
    data: bookYears,
    // isLoading: bookYearsLoading,
    // isError: bookYearsError,
    // isSuccess: bookYearsSuccess,
    // refetch: bookYearsRefetch,
  } = useQuery({
    queryKey: ["book_years"],
    queryFn: () =>
      fetchAll<BookYear>(
        "/nl/v3/objects/fetch_all",
        {
          object: "book_year",
          fields: ["id", "short_visual"],
          order: "id DESC"
        },
        login
      ),
    enabled: !!login
  });

  







  const mutationCalculate = useMutation({
    mutationFn: () => {
      setExportUrl(undefined);
      return fetchOne<RawExportStat>(
        "/nl/v3/objects/custom_action",
        {
          object: 'exit',
          class_action: 'custom_api_exit_stats',
          handler_id: login?.id,
          book_year: bookYear,
          book_year_m1: bookYearMinus1,
          book_year_m2: bookYearMinus2
        },
        login
      );
    },
    onSuccess: (data, variables, context) => {
      const rawB1 = data.custom_result.b1 ? (Object.values(data.custom_result.b1) as any[]) : [];
      const rawB2 = data.custom_result.b2 ? (Object.values(data.custom_result.b2) as any[]) : [];
      const rawB3 = data.custom_result.b3 ? (Object.values(data.custom_result.b3) as any[]) : [];

      // Build a stable order of reasons across all three book years, so the same
      // reason always lands on the same colour in every pie chart. Order by total
      // count (desc) so the most common reasons come first.
      const totals: Record<string, number> = {};
      [...rawB1, ...rawB2, ...rawB3].forEach((stat:any) => {
        if (!stat || !stat.name) return;
        totals[stat.name] = (totals[stat.name] || 0) + (stat.count || 0);
      });
      const masterNames = Object.keys(totals).sort((a, b) => (totals[b] - totals[a]) || a.localeCompare(b));
      const colourFor = (name:string) => COLOURS[Math.max(0, masterNames.indexOf(name)) % COLOURS.length];

      const passes = (stat:any) => !!stat.count && (!onlyActive || (!!onlyActive && !!stat.active));

      // Each pie is rendered in the same master order, with a colour keyed to the
      // reason name — so a reason keeps its colour across all three pie charts.
      const buildPie = (raw:any[]):PieStat[] => raw
        .filter(passes)
        .sort((a, b) => masterNames.indexOf(a.name) - masterNames.indexOf(b.name))
        .map((stat:any) => ({ name: stat.name, value: stat.count, color: colourFor(stat.name) }));

      if (data.custom_result.b1) setPieB1Stats(buildPie(rawB1));
      if (data.custom_result.b2) setPieB2Stats(buildPie(rawB2));
      if (data.custom_result.b3) setPieB3Stats(buildPie(rawB3));

      // Bar chart: one row per reason, a column per book year, same master order.
      const byName: Record<string, BarStat> = {};
      const ensure = (name:string) => {
        if (!byName[name]) byName[name] = { name, value: 0, valueM1: 0, valueM2: 0 };
        return byName[name];
      };
      rawB1.forEach((stat:any) => { if (passes(stat)) ensure(stat.name).value = stat.count; });
      rawB2.forEach((stat:any) => { if (passes(stat)) ensure(stat.name).valueM1 = stat.count; });
      rawB3.forEach((stat:any) => { if (passes(stat)) ensure(stat.name).valueM2 = stat.count; });
      const barStat:BarStat[] = masterNames.filter(name => byName[name]).map(name => byName[name]);

      const sumCounts = (raw:any[]) => raw.filter(passes).reduce((sum:number, s:any) => sum + (s.count || 0), 0);

      setBarStats(barStat);
      setBarTotals({
        b1: sumCounts(rawB1),
        b2: sumCounts(rawB2),
        b3: sumCounts(rawB3)
      });
    },
  });
  const mutationExport = useMutation({
    mutationFn: () => {
      setExportUrl(undefined);
      return fetchOne<RawExportStat>(
        "/nl/v3/objects/custom_action",
        {
          object: 'exit',
          class_action: 'custom_api_exit_stats_export',
          handler_id: login?.id,
          book_year: bookYear,
          book_year_m1: bookYearMinus1,
          book_year_m2: bookYearMinus2,
          only_active: !!onlyActive ? 1 : 0
        },
        login
      );
    },
    onSuccess: (data, variables, context) => {
      setExportUrl(data.custom_result);
    },
  });








  return (<Page sx={{}} title="Exit Stats" helper="exit_stats">
    <Grid container spacing="8">
      <Grid item xs={12} md={4}>
        <StyledSelect
          label="Book Year Base (current)"
          value={bookYear?.toString() || ''}
          id='book_year'
          onChange={(v) => {setBookYear(parseInt(v, 10));}}
          list={[
            {id: '', name: "Unselected"},
            ...((bookYears || []).map(by => {return {id: by.id?.toString() || '', name: by.short_visual};}))
          ]}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <StyledSelect
          label="Book Year Previous"
          value={bookYearMinus1?.toString() || ''}
          id='book_year'
          onChange={(v) => {setBookYearMinus1(parseInt(v, 10));}}
          list={[
            {id: '', name: "Unselected"},
            ...((bookYears || []).map(by => {return {id: by.id?.toString() || '', name: by.short_visual};}))
          ]}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <StyledSelect
          label="Book Year Two Ago"
          value={bookYearMinus2?.toString() || ''}
          id='book_year'
          onChange={(v) => {setBookYearMinus2(parseInt(v, 10));}}
          list={[
            {id: '', name: "Unselected"},
            ...((bookYears || []).map(by => {return {id: by.id?.toString() || '', name: by.short_visual};}))
          ]}
        />
      </Grid>
      <Grid item xs={12}>
        {!!onlyActive && <StyledButton
          label="Also show inactive reasons"
          id='show_inactive'
          contained
          onClick={(v) => {
            setOnlyActive(false);
          }}
          sx={{
            marginRight: 1
          }}
        />}
        {!onlyActive && <StyledButton
          label="Only show active reasons"
          id='show_active'
          contained
          onClick={(v) => {
            setOnlyActive(true);
          }}
          sx={{
            marginRight: 1
          }}
        />}
        <StyledButton
          label="Spin the wheel"
          disabled={!bookYear}
          id='spin'
          contained
          onClick={(v) => {
            mutationCalculate.mutate();
          }}
        />
        <StyledButton
          label="Export the wheeled"
          disabled={!bookYear}
          id='export'
          contained
          onClick={(v) => {
            mutationExport.mutate();
          }}
          sx={{
            marginLeft: 1
          }}
        />
      </Grid>
      {!!mutationCalculate.isPending && <Grid item xs={12}>
        <CircularProgress />
      </Grid>}
      {!!exportUrl && <Grid item xs={12}>
        <Typography>Download your export here: <a href={exportUrl} target="_BLANK">{exportUrl}</a></Typography>
      </Grid>}
      {!!pieB1Stats && <Grid item xs={12}>
        <Typography variant="h3" sx={{textAlign: 'center', textDecoration: 'underline'}}>{bookYears?.filter(fff => fff.id === bookYear)[0].short_visual}</Typography>
        <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, alignItems: 'center', justifyContent: 'center', gap: 2}}>
          <PieChart width={1000} height={400}>
            <Pie data={pieB1Stats} dataKey="value" cx="50%" cy="50%" outerRadius={100} fill="#8884d8">
              {(pieB1Stats || []).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
          <PieLegend
            items={(pieB1Stats || []).map((el, index) => ({
              color: el.color,
              label: `${el.name} (${el.value})`
            }))}
            sx={{minWidth: 300, alignSelf: 'center'}}
          />
        </Box>
      </Grid>}
      {!!pieB2Stats && <Grid item xs={12}>
        <Typography variant="h3" sx={{textAlign: 'center', textDecoration: 'underline'}}>{bookYears?.filter(fff => fff.id === bookYearMinus1)[0].short_visual}</Typography>
        <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, alignItems: 'center', justifyContent: 'center', gap: 2}}>
          <PieChart width={1000} height={400}>
            <Pie data={pieB2Stats} dataKey="value" cx="50%" cy="50%" outerRadius={100} fill="#8884d8">
              {(pieB2Stats || []).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
          <PieLegend
            items={(pieB2Stats || []).map((el, index) => ({
              color: el.color,
              label: `${el.name} (${el.value})`
            }))}
            sx={{minWidth: 300, alignSelf: 'center'}}
          />
        </Box>
      </Grid>}
      {!!pieB3Stats && <Grid item xs={12}>
        <Typography variant="h3" sx={{textAlign: 'center', textDecoration: 'underline'}}>{bookYears?.filter(fff => fff.id === bookYearMinus2)[0].short_visual}</Typography>
        <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, alignItems: 'center', justifyContent: 'center', gap: 2}}>
          <PieChart width={1000} height={400}>
            <Pie data={pieB3Stats} dataKey="value" cx="50%" cy="50%" outerRadius={100} fill="#8884d8">
              {(pieB3Stats || []).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
          <PieLegend
            items={(pieB3Stats || []).map((el, index) => ({
              color: el.color,
              label: `${el.name} (${el.value})`
            }))}
            sx={{minWidth: 300, alignSelf: 'center'}}
          />
        </Box>
      </Grid>}
      {!!barStats && <Grid item xs={12}>
        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2}}>
          <BarChart width={1000} height={600} data={barStats}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" interval={0} angle={-25} textAnchor="end" height={150} />
            <YAxis />
            <Tooltip />
            {/* @ts-ignore */}
            {barTotals.b1 > 0 && <Bar dataKey="value" label={bookYears?.filter(fff => fff.id === bookYear)[0].short_visual} fill={COLOURS[0]} />}
            {/* @ts-ignore */}
            {barTotals.b2 > 0 && <Bar dataKey="valueM1" label={bookYears?.filter(fff => fff.id === bookYearMinus1)[0].short_visual} fill={COLOURS[4]} />}
            {/* @ts-ignore */}
            {barTotals.b3 > 0 && <Bar dataKey="valueM2" label={bookYears?.filter(fff => fff.id === bookYearMinus2)[0].short_visual} fill={COLOURS[7]} />}
          </BarChart>
          <PieLegend
            direction="horizontal"
            items={[
              {color: COLOURS[0], label: barTotals.b1 > 0 ? bookYears?.filter(fff => fff.id === bookYear)[0].short_visual : '-'},
              {color: COLOURS[4], label: barTotals.b2 > 0 ? bookYears?.filter(fff => fff.id === bookYearMinus1)[0].short_visual : '-'},
              {color: COLOURS[7], label: barTotals.b3 > 0 ? bookYears?.filter(fff => fff.id === bookYearMinus2)[0].short_visual : '-'}
            ]}
          />
        </Box>
      </Grid>}
    </Grid>
  </Page>);
}
